/**
 * Lazy-load a module.
 *
 * Optionally only load the module if the provided selector is present.
 *
 * @param path - The path to the module
 * @param [selector] - The CSS selector to query
 *
 * @example
 * // Imports `menu.ts` executes default export if exported
 * loader('menu');
 *
 * @example
 * // Imports `popup.ts` and executes default export for all matches of `.popup`
 * loader('popup', '.popup');
 */
const loader = (path: string, selector?: string) => document.addEventListener('DOMContentLoaded', async () => {
  if (!selector) {
    (await import(`../${path}`))?.default();
    return;
  }

  const elements = document.querySelectorAll(selector);

  [...elements].forEach(async (element: HTMLElement) => {
    (await import(`../${path}.ts`))?.default(element);
  });
});

/**
 * Lazy-load a block's module.
 *
 * @param path - The path to the module
 * @param selector - The CSS selector of the block
 * @param [fileName] - The name of the TS file to load
 *
 * @example
 * // Imports `init.ts` from the example block and executes default export
 * block('example', '.example');
 */
const block = (name: string, selector: string, fileName: string = 'init') => document.addEventListener('DOMContentLoaded', () => {
  const elements = document.querySelectorAll(selector);

  [...elements].forEach(async (element: HTMLElement) => {
    (await import(`../../../templates/block/${name}/${fileName}.ts`)).default(element);
  });
});

export default loader;
export { block, loader };
